/**
 * 自动选线：并发探测全部线路，按 servers.ts 的规则选一条并记住。
 *
 * 拆成单独文件是因为它有副作用（网络 + 存储），servers.ts 里的 choose 保持纯函数好测。
 */

import { choose, currentServer, pinnedServer, probe, serverList, setCurrentServer, setServerList } from './servers'
import type { ProbeResult, ServerInfo } from './servers'

export interface LineStatus {
  server: ServerInfo | null
  probes: ProbeResult[]
}

/** 探一遍全部线路并选定。`exclude` 是刚失败的线路（失效切换时跳过它）。 */
export async function autoSelect(exclude: string | null = null): Promise<LineStatus> {
  const list = serverList()
  const probes = await Promise.all(list.map((s) => probe(s)))
  const server = choose(list, probes, pinnedServer(), exclude)
  if (server) {
    setCurrentServer(server.id)
    void refreshListFrom(server.base)
  }
  return { server, probes }
}

/**
 * 从选中的服务器拉最新线路表。新增备机、换域名都靠它下发，不必发新版本。
 * 拉不到或格式不对就沿用现有的，绝不能因为这一步失败而影响登录。
 */
export async function refreshListFrom(base: string): Promise<boolean> {
  try {
    const r = await fetch(`${base}/servers.json`, { cache: 'no-store' })
    if (!r.ok) return false
    return acceptPushedList(await r.json(), base)
  } catch {
    return false
  }
}

/**
 * 下发的表必须包含「发它的那台服务器」才采纳（纯函数，便于单测）。
 * 否则某台服务器的 servers.json 配错，就能把正连着它的用户整个切走；
 * 本地联调时也会被正式表覆盖，当前线路凭空消失。
 */
export function acceptPushedList(list: unknown, fromBase: string): boolean {
  if (!Array.isArray(list)) return false
  const has = list.some((s) => s && typeof s === 'object' && (s as ServerInfo).base === fromBase)
  return has ? setServerList(list) : false
}

/** 当前线路是否还活着（登录前快速确认一次，别等用户点了才发现要换线）。 */
export async function currentAlive(): Promise<boolean> {
  return (await probe(currentServer())).ok
}
